/**
 * @file UiContext.jsx
 * Contesto di stato UI condiviso tra le feature (griglia Excel, merge, workbench, setup).
 */

import { createContext, useReducer, useCallback } from 'react';
/**
 * UI state context for managing interactive UI elements across features.
 * Handles selection, editing, scrolling state for Excel grid, merge view state,
 * workbench tree state, and setup table selection.
 * 
 * Sections:
 * - excel: Cell selection, editing mode, range selection, scroll position
 * - merge: Split ratio, pinned state, row highlighting
 * - workbench: Tree node expansion, selected node, filter text
 * - setup: Active table selection
 */

export const UiContext = createContext(null);

const initialState = {
  excel: {
    selectedCell:   null,
    editingCell:    null,
    isSelecting:    false,
    selectedRange:  null,
    scrollPosition: { x: 0, y: 0 },
  },
  merge: {
    splitRatio:    50,
    isPinned:      false,
    highlightedRow: null,
  },
  workbench: {
    expandedNodes: {},
    selectedNode:  null,
    filterText:    '',
  },
  setup: {
    activeTable: null,
  },
};

function uiReducer(state, action) {
  switch (action.type) {
    case 'EXCEL_SELECT_CELL':
      return { ...state, excel: { ...state.excel, selectedCell: action.cell, editingCell: null } };
    case 'EXCEL_START_EDIT':
      return { ...state, excel: { ...state.excel, editingCell: action.cell } };
    case 'EXCEL_STOP_EDIT':
      return { ...state, excel: { ...state.excel, editingCell: null } };
    case 'EXCEL_SET_SCROLL':
      return { ...state, excel: { ...state.excel, scrollPosition: action.position } };

    case 'MERGE_SET_SPLIT':
      return { ...state, merge: { ...state.merge, splitRatio: action.ratio } };
    case 'MERGE_TOGGLE_PIN':
      return { ...state, merge: { ...state.merge, isPinned: !state.merge.isPinned } };
    case 'MERGE_HIGHLIGHT_ROW':
      return { ...state, merge: { ...state.merge, highlightedRow: action.row } };

    case 'WORKBENCH_TOGGLE_NODE': {
      const expanded = { ...state.workbench.expandedNodes };
      if (expanded[action.nodeId]) delete expanded[action.nodeId];
      else expanded[action.nodeId] = true;
      return { ...state, workbench: { ...state.workbench, expandedNodes: expanded } };
    }
    case 'WORKBENCH_SELECT_NODE':
      return { ...state, workbench: { ...state.workbench, selectedNode: action.nodeId } };
    case 'WORKBENCH_SET_FILTER':
      return { ...state, workbench: { ...state.workbench, filterText: action.text } };

    case 'SETUP_SET_TABLE':
      return { ...state, setup: { ...state.setup, activeTable: action.table } };

    default:
      return state;
  }
}

export function UiProvider({ children }) {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  const excelActions = {
    selectCell: useCallback((cell)     => dispatch({ type: 'EXCEL_SELECT_CELL', cell }), []),
    startEdit:  useCallback((cell)     => dispatch({ type: 'EXCEL_START_EDIT', cell }), []),
    stopEdit:   useCallback(()         => dispatch({ type: 'EXCEL_STOP_EDIT' }), []),
    setScroll:  useCallback((position) => dispatch({ type: 'EXCEL_SET_SCROLL', position }), []),
  };

  const mergeActions = {
    setSplit:     useCallback((ratio) => dispatch({ type: 'MERGE_SET_SPLIT', ratio }), []),
    togglePin:    useCallback(()      => dispatch({ type: 'MERGE_TOGGLE_PIN' }), []),
    highlightRow: useCallback((row)   => dispatch({ type: 'MERGE_HIGHLIGHT_ROW', row }), []),
  };

  const workbenchActions = {
    toggleNode: useCallback((nodeId) => dispatch({ type: 'WORKBENCH_TOGGLE_NODE', nodeId }), []),
    selectNode: useCallback((nodeId) => dispatch({ type: 'WORKBENCH_SELECT_NODE', nodeId }), []),
    setFilter:  useCallback((text)   => dispatch({ type: 'WORKBENCH_SET_FILTER', text }), []),
  };

  const setupActions = {
    setTable: useCallback((table) => dispatch({ type: 'SETUP_SET_TABLE', table }), []),
  };

  return (
    <UiContext.Provider value={{
      excel:     { ...state.excel,     ...excelActions },
      merge:     { ...state.merge,     ...mergeActions },
      workbench: { ...state.workbench, ...workbenchActions },
      setup:     { ...state.setup,     ...setupActions },
    }}>
      {children}
    </UiContext.Provider>
  );
}
